import { useMemo, useState } from "react";
import { styles } from "../styles";
import { hasCapability } from "../utils/capabilities";
import { createVendor, PAYMENT_TERMS_OPTIONS } from "../models/models";
import BackButton from "../components/BackButton";

const STATUS_LABEL = {
  active: "Active",
  inactive: "Inactive",
  on_hold: "On Hold",
};

const STATUS_COLOR = {
  active: { bg: "#d6f5dd", color: "#1a7f37" },
  inactive: { bg: "#f3f4f6", color: "#6b7280" },
  on_hold: { bg: "#fff3cd", color: "#92400e" },
};

const formatTerms = (key) =>
  PAYMENT_TERMS_OPTIONS.find((o) => o.value === key)?.label || key || "—";

// Date math for W-9 / COI expiration badges. Returns null when no date
// set; otherwise { label, palette } so the UI can color-code "expiring
// soon" (within 30 days) and "expired" (past today). Parses YYYY-MM-DD
// as local midnight so the boundary doesn't TZ-shift by a day.
const expiryBadge = (dateStr, label) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map((s) => parseInt(s, 10));
  if (!y || !m || !d) return null;
  const exp = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((exp.getTime() - today.getTime()) / 86400000);
  if (diff < 0) {
    return {
      text: `${label} EXPIRED ${dateStr}`,
      bg: "#fee2e2",
      color: "#b91c1c",
      border: "#fca5a5",
    };
  }
  if (diff <= 30) {
    return {
      text: `${label} expires ${dateStr} (${diff}d)`,
      bg: "#fff3cd",
      color: "#92400e",
      border: "#facc15",
    };
  }
  return {
    text: `${label} on file (exp ${dateStr})`,
    bg: "#d6f5dd",
    color: "#1a7f37",
    border: "#a7e3b3",
  };
};

// Vendor master at organizations/{orgId}/vendors. Phase A0 of the
// Finance module. POs reference vendors by id; po.vendorName denormalized
// at write time so renames don't rewrite PO history. Per
// [[project-finance-ar-module]] + [[feedback-alphabetical-lists]] —
// sorted alphabetically by displayName / name.
export default function VendorsPage({
  vendorsRaw = [],
  actions,
  role,
  capabilities,
  onBack,
}) {
  const canManage = hasCapability(role, capabilities, "manageVendors");

  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  // create-form state — minimal: name + payment terms + status. The
  // expanded Edit form unlocks every field.
  const [newName, setNewName] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newPaymentTerms, setNewPaymentTerms] = useState("net30");
  const [newAccountNumber, setNewAccountNumber] = useState("");
  const [newApContactName, setNewApContactName] = useState("");
  const [newApContactEmail, setNewApContactEmail] = useState("");

  // edit-form state — populated on Edit click. Holds every field for the
  // single vendor being edited.
  const [ed, setEd] = useState({});

  const resetCreate = () => {
    setNewName("");
    setNewDisplayName("");
    setNewPaymentTerms("net30");
    setNewAccountNumber("");
    setNewApContactName("");
    setNewApContactEmail("");
    setShowCreate(false);
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const v = {
        ...createVendor(newName.trim()),
        displayName: newDisplayName.trim() || newName.trim(),
        paymentTerms: newPaymentTerms,
        accountNumber: newAccountNumber.trim(),
        apContactName: newApContactName.trim(),
        apContactEmail: newApContactEmail.trim(),
      };
      await actions.addVendor(v);
      resetCreate();
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setExpandedId(v.id);
    setEd({ ...v });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEd({});
  };
  const saveEdit = async () => {
    if (!ed.name?.trim()) return;
    setBusy(true);
    try {
      await actions.updateVendor(editingId, {
        ...ed,
        displayName: ed.displayName?.trim() || ed.name.trim(),
      });
      cancelEdit();
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (v) => {
    if (
      !window.confirm(
        `Delete vendor "${v.displayName || v.name}"?\n\n` +
          `Past POs that referenced this vendor will keep the vendor name on the PO record (denormalized).\n\n` +
          `If you have an ongoing relationship and just want to retire them, switch their status to "Inactive" instead.`
      )
    )
      return;
    await actions.deleteVendor(v.id);
  };

  // Alphabetical sort + status + search filter.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...vendorsRaw]
      .filter((v) => statusFilter === "all" || v.status === statusFilter)
      .filter((v) => {
        if (!term) return true;
        const hay = [
          v.name,
          v.displayName,
          v.dba,
          v.accountNumber,
          v.apContactName,
          v.apContactEmail,
          v.salesContactName,
          ...(v.categories || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(term);
      })
      .sort((a, b) =>
        (a.displayName || a.name || "")
          .toLowerCase()
          .localeCompare((b.displayName || b.name || "").toLowerCase())
      );
  }, [vendorsRaw, search, statusFilter]);

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Vendors</h2>

      {canManage && (
        <button
          style={styles.button}
          onClick={() => setShowCreate((s) => !s)}
        >
          {showCreate ? "Cancel New Vendor" : "+ New Vendor"}
        </button>
      )}

      {showCreate && canManage && (
        <div style={styles.card}>
          <h3 style={{ marginTop: 0 }}>New vendor</h3>
          <p style={{ fontSize: "12px", color: "#666", marginTop: 0 }}>
            Just the basics here. Open the vendor and click Edit for full
            contact / W-9 / COI / categories.
          </p>
          <input
            style={styles.input}
            placeholder="Vendor name (legal name on W-9)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="Short label for dropdowns (optional)"
            value={newDisplayName}
            onChange={(e) => setNewDisplayName(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="Our account # with them (optional)"
            value={newAccountNumber}
            onChange={(e) => setNewAccountNumber(e.target.value)}
          />
          <label style={{ fontSize: "13px" }}>Payment terms: </label>
          <select
            value={newPaymentTerms}
            onChange={(e) => setNewPaymentTerms(e.target.value)}
          >
            {PAYMENT_TERMS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <br />
          <input
            style={styles.input}
            placeholder="AP contact name (optional)"
            value={newApContactName}
            onChange={(e) => setNewApContactName(e.target.value)}
          />
          <input
            style={styles.input}
            placeholder="AP contact email (optional)"
            value={newApContactEmail}
            onChange={(e) => setNewApContactEmail(e.target.value)}
          />
          <button
            style={styles.button}
            onClick={handleCreate}
            disabled={busy || !newName.trim()}
          >
            {busy ? "Saving..." : "Save Vendor"}
          </button>
        </div>
      )}

      <div style={{ ...styles.card }}>
        <input
          style={styles.input}
          placeholder="Search name, DBA, contact, account #, category"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ marginTop: "6px", fontSize: "13px" }}>
          <label>Status: </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="on_hold">On Hold</option>
          </select>
          <span style={{ marginLeft: "10px", color: "#666" }}>
            {visible.length} of {vendorsRaw.length} shown
          </span>
        </div>
      </div>

      {visible.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>
          {vendorsRaw.length === 0
            ? "No vendors yet. Add your first one above."
            : "No vendors match the current filters."}
        </p>
      ) : (
        visible.map((v) => {
          const open = expandedId === v.id;
          const editing = editingId === v.id;
          const statusPalette = STATUS_COLOR[v.status] || STATUS_COLOR.active;
          const w9Badge =
            v.w9OnFile && expiryBadge(v.w9ExpiresOn, "W-9");
          const coiBadge =
            v.coiOnFile && expiryBadge(v.coiExpiresOn, "COI");
          return (
            <div key={v.id} style={styles.card}>
              <div
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setExpandedId(open && !editing ? null : v.id)
                }
              >
                {open ? "▾" : "▸"}{" "}
                <strong>{v.displayName || v.name}</strong>
                <span
                  style={{
                    fontSize: "11px",
                    marginLeft: "8px",
                    padding: "1px 6px",
                    background: statusPalette.bg,
                    color: statusPalette.color,
                    borderRadius: "4px",
                  }}
                >
                  {STATUS_LABEL[v.status] || v.status}
                </span>
                {v.is1099Eligible && (
                  <span
                    style={{
                      fontSize: "11px",
                      marginLeft: "6px",
                      padding: "1px 6px",
                      background: "#e0eaff",
                      color: "#1a5fb4",
                      borderRadius: "4px",
                    }}
                    title="Will appear on year-end 1099 reporting"
                  >
                    1099
                  </span>
                )}
                {w9Badge && (
                  <span
                    style={{
                      fontSize: "11px",
                      marginLeft: "6px",
                      padding: "1px 6px",
                      background: w9Badge.bg,
                      color: w9Badge.color,
                      border: `1px solid ${w9Badge.border}`,
                      borderRadius: "4px",
                    }}
                  >
                    {w9Badge.text}
                  </span>
                )}
                {coiBadge && (
                  <span
                    style={{
                      fontSize: "11px",
                      marginLeft: "6px",
                      padding: "1px 6px",
                      background: coiBadge.bg,
                      color: coiBadge.color,
                      border: `1px solid ${coiBadge.border}`,
                      borderRadius: "4px",
                    }}
                  >
                    {coiBadge.text}
                  </span>
                )}
                <br />
                <span style={{ fontSize: "12px", color: "#666", marginLeft: "14px" }}>
                  {formatTerms(v.paymentTerms)}
                  {v.accountNumber ? ` • Acct ${v.accountNumber}` : ""}
                  {v.apContactName ? ` • AP: ${v.apContactName}` : ""}
                </span>
              </div>

              {open && !editing && (
                <div style={{ marginLeft: "14px", marginTop: "6px", fontSize: "13px" }}>
                  {v.dba && (
                    <div>
                      <strong>DBA:</strong> {v.dba}
                    </div>
                  )}
                  {(v.street || v.city) && (
                    <div>
                      <strong>Address:</strong> {v.street}
                      {v.city ? `, ${v.city}` : ""}
                      {v.state ? `, ${v.state}` : ""}
                      {v.zip ? ` ${v.zip}` : ""}
                    </div>
                  )}
                  {v.apContactEmail && (
                    <div>
                      <strong>AP email:</strong> {v.apContactEmail}
                    </div>
                  )}
                  {v.apContactPhone && (
                    <div>
                      <strong>AP phone:</strong> {v.apContactPhone}
                    </div>
                  )}
                  {v.salesContactName && (
                    <div>
                      <strong>Sales contact:</strong> {v.salesContactName}
                      {v.salesContactEmail ? ` (${v.salesContactEmail})` : ""}
                      {v.salesContactPhone ? ` • ${v.salesContactPhone}` : ""}
                    </div>
                  )}
                  {v.taxId && (
                    <div>
                      <strong>Tax ID:</strong> {v.taxId}
                    </div>
                  )}
                  {(v.categories || []).length > 0 && (
                    <div>
                      <strong>Categories:</strong> {(v.categories || []).join(", ")}
                    </div>
                  )}
                  {v.notes && (
                    <div style={{ marginTop: "4px" }}>
                      <strong>Notes:</strong>
                      <div style={{ whiteSpace: "pre-wrap" }}>{v.notes}</div>
                    </div>
                  )}
                  {canManage && (
                    <div style={{ marginTop: "8px" }}>
                      <button style={styles.button} onClick={() => startEdit(v)}>
                        Edit
                      </button>
                      <button
                        style={{ ...styles.button, color: "red" }}
                        onClick={() => handleDelete(v)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )}

              {open && editing && (
                <div
                  style={{
                    marginLeft: "14px",
                    marginTop: "8px",
                    fontSize: "13px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "8px",
                  }}
                >
                  <label>
                    Legal name
                    <input
                      style={styles.input}
                      value={ed.name || ""}
                      onChange={(e) => setEd({ ...ed, name: e.target.value })}
                    />
                  </label>
                  <label>
                    Display / short label
                    <input
                      style={styles.input}
                      value={ed.displayName || ""}
                      onChange={(e) =>
                        setEd({ ...ed, displayName: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    DBA
                    <input
                      style={styles.input}
                      value={ed.dba || ""}
                      onChange={(e) => setEd({ ...ed, dba: e.target.value })}
                    />
                  </label>
                  <label>
                    Our account #
                    <input
                      style={styles.input}
                      value={ed.accountNumber || ""}
                      onChange={(e) =>
                        setEd({ ...ed, accountNumber: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Status
                    <select
                      value={ed.status || "active"}
                      onChange={(e) => setEd({ ...ed, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="on_hold">On Hold</option>
                    </select>
                  </label>
                  <label>
                    Payment terms
                    <select
                      value={ed.paymentTerms || "net30"}
                      onChange={(e) =>
                        setEd({ ...ed, paymentTerms: e.target.value })
                      }
                    >
                      {PAYMENT_TERMS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Street
                    <input
                      style={styles.input}
                      value={ed.street || ""}
                      onChange={(e) => setEd({ ...ed, street: e.target.value })}
                    />
                  </label>
                  <label>
                    City
                    <input
                      style={styles.input}
                      value={ed.city || ""}
                      onChange={(e) => setEd({ ...ed, city: e.target.value })}
                    />
                  </label>
                  <label>
                    State
                    <input
                      style={styles.input}
                      value={ed.state || ""}
                      onChange={(e) => setEd({ ...ed, state: e.target.value })}
                    />
                  </label>
                  <label>
                    Zip
                    <input
                      style={styles.input}
                      value={ed.zip || ""}
                      onChange={(e) => setEd({ ...ed, zip: e.target.value })}
                    />
                  </label>
                  <label>
                    AP contact name
                    <input
                      style={styles.input}
                      value={ed.apContactName || ""}
                      onChange={(e) =>
                        setEd({ ...ed, apContactName: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    AP contact email
                    <input
                      style={styles.input}
                      value={ed.apContactEmail || ""}
                      onChange={(e) =>
                        setEd({ ...ed, apContactEmail: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    AP contact phone
                    <input
                      style={styles.input}
                      value={ed.apContactPhone || ""}
                      onChange={(e) =>
                        setEd({ ...ed, apContactPhone: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Sales contact name
                    <input
                      style={styles.input}
                      value={ed.salesContactName || ""}
                      onChange={(e) =>
                        setEd({ ...ed, salesContactName: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Sales contact email
                    <input
                      style={styles.input}
                      value={ed.salesContactEmail || ""}
                      onChange={(e) =>
                        setEd({ ...ed, salesContactEmail: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Sales contact phone
                    <input
                      style={styles.input}
                      value={ed.salesContactPhone || ""}
                      onChange={(e) =>
                        setEd({ ...ed, salesContactPhone: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Tax ID / EIN
                    <input
                      style={styles.input}
                      value={ed.taxId || ""}
                      onChange={(e) => setEd({ ...ed, taxId: e.target.value })}
                    />
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={!!ed.is1099Eligible}
                      onChange={(e) =>
                        setEd({ ...ed, is1099Eligible: e.target.checked })
                      }
                    />{" "}
                    1099-eligible (service vendor)
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={!!ed.w9OnFile}
                      onChange={(e) =>
                        setEd({ ...ed, w9OnFile: e.target.checked })
                      }
                    />{" "}
                    W-9 on file
                  </label>
                  <label>
                    W-9 expires
                    <input
                      type="date"
                      style={styles.input}
                      value={ed.w9ExpiresOn || ""}
                      onChange={(e) =>
                        setEd({ ...ed, w9ExpiresOn: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={!!ed.coiOnFile}
                      onChange={(e) =>
                        setEd({ ...ed, coiOnFile: e.target.checked })
                      }
                    />{" "}
                    COI on file
                  </label>
                  <label>
                    COI expires
                    <input
                      type="date"
                      style={styles.input}
                      value={ed.coiExpiresOn || ""}
                      onChange={(e) =>
                        setEd({ ...ed, coiExpiresOn: e.target.value })
                      }
                    />
                  </label>
                  <label style={{ gridColumn: "1 / -1" }}>
                    Categories (comma-separated)
                    <input
                      style={styles.input}
                      value={(ed.categories || []).join(", ")}
                      onChange={(e) =>
                        setEd({
                          ...ed,
                          categories: e.target.value
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  </label>
                  <label style={{ gridColumn: "1 / -1" }}>
                    Notes
                    <textarea
                      style={styles.textarea}
                      value={ed.notes || ""}
                      onChange={(e) => setEd({ ...ed, notes: e.target.value })}
                    />
                  </label>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <button
                      style={styles.button}
                      onClick={saveEdit}
                      disabled={busy || !ed.name?.trim()}
                    >
                      {busy ? "Saving..." : "Save"}
                    </button>
                    <button style={styles.button} onClick={cancelEdit}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </>
  );
}
